import { GiftLimits } from '@sola/schema/dotcom/proxy-api/giftLimits.js';
import { PaymentMethodItemDTO } from '@sola/schema/dotcom/proxy-api/paymentMethods.js';
import { Supporter } from '@sola/schema/dotcom/proxy-api/supporter.js';
import { SupporterPaymentMethodDetails } from '@sola/schema/dotcom/supporters/paymentMethods.js';
import { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { userErrorBadParam } from '@sola/shared-utils/errors/misc.js';
import {
  userErrorPaymentMethodCreate,
  userErrorPaymentMethodDelete,
  userErrorPaymentMethodUpdate,
} from '@sola/shared-utils/errors/supporters.js';
import { GetOptions } from '@sola/shared-utils/httpClient.js';
import { isObject } from '@sola/shared-utils/misc.js';

import {
  createSupporterPaymentMethod,
  deleteSupporterPaymentMethod,
  getSupporterById,
  getSupporterByIdWithOptions,
  updateSupporterPaymentMethod,
} from './utils/routes';
import { fetchSupporterAttribute } from './utils/utils';

// import { getSupporterFromCache, saveSupporterToCache } from './persistence';

// const CACHED_SUPPORTER_ID_PREFIX = 'authUser';

/**
 * Get Supporter ID from localStorage, if not present return null. Sometimes the
 * supporter id will not be present in localStorage, hence why some
 * supporter functions will have the ability to receive a supporter ID
 *  */
// export function getSupporterId(
//   scope: 'internalId' | 'globalId',
// ): string | null {
//   const supporterId =
//     sessionStorage.getItem(`${CACHED_SUPPORTER_ID_PREFIX}.${scope}`) || null;

//   if (supporterId) {
//     return supporterId;
//   }

//   /** TODO: Clear cookies & session storage here too, because
//   if this function returns null, it means the auth user
//   is not present in the cookies, thus it's expired. */
//   return null;
// }

/**
 * Check for and retrieve supporter by Internal / SFUS Account ID
 * GET /supporters/{supporterId}
 */
export const getSupporter = async (
  supporterId?: string | null,
): Promise<Supporter | null> => {
  if (!supporterId) {
    throw userErrorBadParam('supporterId');
  }

  // TODO: I didn't think we were using localStorage at the moment, correct?
  // // Check if supporter in storage
  // const cachedSupporter = !isServerSide() && getSupporterFromCache();

  // if (cachedSupporter) {
  // 	return cachedSupporter;
  // }

  const supporter = await getSupporterById(supporterId);

  if (!supporter) {
    return null;
  }

  // TODO: Not currently using localStorage for this data for now
  // // save to cache
  // if (supporter && !isServerSide()) {
  // 	saveSupporterToCache(supporter);
  // }

  return supporter;
};

/**
 * Get supporter by Internal / SFUS Account ID - passing an auth cookie
 * Used for server-to-server call where one server needs to pass the cookie along to another
 * GET /supporters/{supporterId}
 */
export const getSupporterWithAuth = async (
  supporterId?: string | null,
  authCookie?: string,
): Promise<Supporter | undefined> => {
  if (!supporterId) {
    throw userErrorBadParam('supporterId');
  }

  if (!authCookie) {
    throw userErrorBadParam('authCookie');
  }

  const options: GetOptions = {
    headers: {
      // Feels like this should be an array, not a string...
      Cookie: authCookie,
      // Cookie: `${COOKIE_NAME_AUTH_USER_SESSION}=${authCookie}`
    },
  };

  const supporter = await getSupporterByIdWithOptions(supporterId, options);

  return supporter;
};

/**
 * Check for and retrieve supporter by global ID's gift limits
 * GET /supporters/{supporterId}/gifts-limits
 */
export const getSupporterGiftsLimits = async (
  supporterId?: string | null,
): Promise<GiftLimits | null | undefined> => {
  if (!supporterId) {
    throw userErrorBadParam('supporterId');
  }

  const supporterGiftLimits = await fetchSupporterAttribute<GiftLimits>(
    supporterId,
    'gifts-limits',
    // 'giftsLimits',
  );

  if (supporterGiftLimits) {
    return supporterGiftLimits;
  }

  return null;
};

/**
 * Get a supporter's givinghs.
 */
export const getSupporterCommitments = async (lookupId: string) => {
  if (!lookupId) {
    throw userErrorBadParam('lookupId');
  }

  return await fetchSupporterAttribute<PaymentMethodItemDTO[]>(
    lookupId,
    'commitments',
  );
};

/**
 * Check for and retrieve supporter payment methods
 * GET /supporters/{supporterId}/payment-methods
 */
export const getSupporterPaymentMethods = async (
  supporterId?: string | null,
) => {
  if (!supporterId) {
    throw userErrorBadParam('supporterId');
  }

  const supporterPaymentMethods = await fetchSupporterAttribute<
    PaymentMethodItemDTO[]
  >(
    supporterId,
    'payment-methods',
    // 'paymentMethods'
  );

  if (!supporterPaymentMethods) {
    return null;
  }

  return supporterPaymentMethods;
};

/**
 * Add a payment method to a supporter.
 * POST /supporters/{supporterId}/payment-methods
 * @param supporterId The supporter's Id.
 * @param paymentMethodDetails The payment method details.
 * @returns The added payment method with augmented information.
 */
export const addSupporterPaymentMethod = async (
  supporterId: string,
  paymentMethodDetails: SupporterPaymentMethodDetails,
): Promise<GraphQLSupporterPaymentMethod | undefined> => {
  if (!supporterId) {
    throw userErrorBadParam('supporterId');
  }

  if (!paymentMethodDetails) {
    throw userErrorBadParam('paymentMethodDetails');
  }

  const { response, statusCode } = await createSupporterPaymentMethod(
    supporterId,
    paymentMethodDetails,
  );

  if (statusCode !== 201) {
    if (isObject(response) && 'errors' in response) {
      throw response.errors[0];
    }

    throw userErrorPaymentMethodCreate(statusCode);
  }

  return response as GraphQLSupporterPaymentMethod;
};

/**
 * Update a payment method for a supporter.
 * PUT /supporters/{supporterId}/payment-methods/{paymentMethodId}
 * @param supporterId The supporter's Id.
 * @param paymentMethod The payment method to update.
 * @returns The updated payment method.
 */
export const saveSupporterPaymentMethod = async (
  supporterId: string,
  paymentMethod: GraphQLSupporterPaymentMethod,
): Promise<GraphQLSupporterPaymentMethod> => {
  if (!supporterId) {
    throw userErrorBadParam('supporterId');
  }

  if (!paymentMethod) {
    throw userErrorBadParam('paymentMethod');
  }

  if (!paymentMethod.id) {
    throw userErrorBadParam('paymentMethod.id');
  }

  const { response, statusCode } = await updateSupporterPaymentMethod(
    supporterId,
    paymentMethod.id,
    paymentMethod,
  );

  if (statusCode !== 200) {
    if (isObject(response) && 'errors' in response) {
      throw response.errors[0];
    }

    throw userErrorPaymentMethodUpdate(statusCode);
  }

  return response as GraphQLSupporterPaymentMethod;
};

export const removeSupporterPaymentMethod = async (
  supporterId?: string,
  paymentMethodId?: string,
) => {
  // TODO: re-evealuate throwing here to React Query.
  if (!supporterId) {
    throw userErrorBadParam('supporterId');
  }

  if (!paymentMethodId) {
    throw userErrorBadParam('paymentMethodId');
  }

  const { response, statusCode } = await deleteSupporterPaymentMethod(
    supporterId,
    paymentMethodId,
  );

  if (statusCode !== 204) {
    if (isObject(response) && response.errors) {
      // TODO: re-evealuate throwing resposne errors
      throw response.errors;
    }

    throw userErrorPaymentMethodDelete(statusCode);
  }

  return paymentMethodId;
};
